/**
 * bt-leasing.js
 * Custom element: <bt-leasing>
 * Handles: modal open/close, provider switching, DSL parse, validation,
 *          cart AJAX (attributes + fee line), eligibility guard, checkout gate.
 */

class BTLeasing extends HTMLElement {
  connectedCallback() {
    // Moving re-triggers connectedCallback, so init runs once in the new spot
    if (!this._placed) {
      this._placed = true;
      const ctas = document.querySelector('#main-cart-footer .cart__ctas');
      if (ctas && !ctas.contains(this)) {
        ctas.prepend(this);
        return;
      }
    }

    // Cache DOM nodes
    this._modal = this.querySelector('.bt-leasing__modal');
    this._overlay = this.querySelector('.bt-leasing__modal-overlay');
    this._form = this.querySelector('.bt-leasing__form');
    this._providerSelect = this.querySelector('[name="provider"]');
    this._providerFieldsEl = this.querySelector('.bt-leasing__provider-fields');
    this._submitBtn = this.querySelector('.bt-leasing__submit-btn');
    this._openBtn = this.querySelector('.bt-leasing__open-btn');
    this._editBtn = this.querySelector('.bt-leasing__edit-btn');
    this._removeBtn = this.querySelector('.bt-leasing__remove-btn');
    this._errorEl = this.querySelector('.bt-leasing__error');

    // Cart footer styles and stacking context break the modal, so host it on body
    if (this._modal && this._modal.parentElement !== document.body) {
      this.classList.forEach((c) => c.startsWith('color-') && this._modal.classList.add(c));
      document.body.appendChild(this._modal);
    }

    const stringsScript = this.querySelector('[data-leasing-strings]');
    this._strings = stringsScript ? JSON.parse(stringsScript.textContent) : {};

    // Config from data attributes
    this._feeVariantId = this.dataset.feeVariantId;
    this._feeSku = this.dataset.feeSku;
    this._triggerAttribute = this.dataset.triggerAttribute || 'Leasing Provider';
    this._eligibleSkus = (this.dataset.eligibleSkus || '')
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    this._mixedNoticeTemplate = this.dataset.mixedNotice || '';

    // Parse provider definitions from embedded JSON
    const providerScript = this.querySelector('[data-leasing-providers]');
    this._providers = providerScript ? JSON.parse(providerScript.textContent) : [];

    // Parse saved attribute values for rehydration
    const attrScript = this.querySelector('[data-leasing-attributes]');
    this._savedAttributes = attrScript ? JSON.parse(attrScript.textContent) : {};

    this._cartCache = null;
    this._cartCacheTime = 0;
    this._cartRefreshPromise = null;
    this._boundCloseOnEsc = this._onKeydown.bind(this);

    this._bindEvents();
    this._rehydrate();

    // Validate current cart state on every page load — clears orphaned leasing
    // state that may have been left from a previous session (e.g. user added a
    // non-eligible item after selecting leasing, then refreshed mid-flight).
    this._onCartUpdate();
  }

  // ─── Events ────────────────────────────────────────────────────────────────

  _bindEvents() {
    this._openBtn?.addEventListener('click', () => this.openModal());
    this._editBtn?.addEventListener('click', () => this.openModal());
    this._removeBtn?.addEventListener('click', () => this._remove());

    this._overlay?.addEventListener('click', () => this.closeModal());
    this._modal?.querySelectorAll('[data-close-modal]').forEach((el) => {
      if (el !== this._overlay) el.addEventListener('click', () => this.closeModal());
    });

    this._providerSelect?.addEventListener('change', () => this._onProviderChange());
    this._form?.addEventListener('input', () => this._validate());
    this._form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this._submit();
    });

    // Intercept cart form submit for the consistency guard
    document.querySelectorAll('form[action="/cart"]').forEach((cartForm) => {
      cartForm.addEventListener('submit', this._onCartFormSubmit.bind(this));
    });

    // Re-run eligibility when cart updates
    if (!this._cartUpdateHandler) {
      this._cartUpdateHandler = () => this._onCartUpdate();
      subscribe(PUB_SUB_EVENTS.cartUpdate, this._cartUpdateHandler);
    }
  }

  _onKeydown(e) {
    if (e.key === 'Escape') this.closeModal();
  }

  _onCartFormSubmit(e) {
    const isPartial = this.dataset.isPartial === 'true';
    const cartState = this.dataset.cartState || 'empty';
    const leasingActive = this.dataset.leasingActive === 'true';

    if (isPartial) {
      e.preventDefault();
      this.openModal();
      this._showError(this._strings.partial_state_warning || '');
      return;
    }

    // Block checkout if cart is no longer eligible but leasing fee/attributes
    // are still present (can happen if JS clear failed or page was refreshed).
    if (cartState !== 'only-eligible' && leasingActive) {
      e.preventDefault();
      // Actively clear the stale state, then let the user retry.
      this._clearAttributes().then(() => {
        this._showError(
          this._strings.ineligible_checkout_block || ''
        );
      });
    }
  }

  _onCartUpdate() {
    const now = Date.now();
    if (this._cartCache && now - this._cartCacheTime < 1500) {
      this._applyCartStateFromCache();
      return;
    }

    if (this._cartRefreshPromise) {
      return this._cartRefreshPromise;
    }

    this._cartRefreshPromise = fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        this._cartCache = cart;
        this._cartCacheTime = Date.now();
        this._applyCartStateFromCart(cart);
        return cart;
      })
      .finally(() => {
        this._cartRefreshPromise = null;
      });

    return this._cartRefreshPromise;
  }

  _applyCartStateFromCache() {
    if (!this._cartCache) return;
    this._applyCartStateFromCart(this._cartCache);
  }

  _applyCartStateFromCart(cart) {
    const { state, eligibleTitles } = this._getCartState(cart.items);
    this._applyCartState(state, eligibleTitles);

    const feeInCart = this._feeVariantId
      ? cart.items.some((item) => String(item.variant_id) === String(this._feeVariantId))
      : false;
    const provider = cart.attributes?.[this._triggerAttribute] || '';

    // Auto-clear leasing when cart is no longer eligible for it
    if (state !== 'only-eligible' && (provider || feeInCart)) {
      this._clearAttributes();
      return;
    }

    // Only flag partial if a fee variant is configured — without one the fee
    // side of the contract cannot be fulfilled and would always appear missing.
    const isPartial = this._feeVariantId
      ? (provider && !feeInCart) || (!provider && feeInCart)
      : false;

    // Auto-recover: provider is saved but fee is missing (e.g. fee_variant_id
    // was blank when leasing was first confirmed, then later set). Re-add fee
    // silently so the user doesn't have to remove and re-confirm leasing.
    if (isPartial && this._feeVariantId && provider && !feeInCart) {
      this._ensureFeeInCart()
        .then(() => {
          this.dataset.isPartial = 'false';
          this.dataset.leasingActive = 'true';
          this._renderPartialWarning(false);
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bt-leasing' });
        })
        .catch(() => {
          // Can't auto-recover — fall back to showing the warning
          this.dataset.isPartial = 'true';
          this._renderPartialWarning(true);
        });
      return;
    }

    this.dataset.isPartial = isPartial ? 'true' : 'false';
    this._renderPartialWarning(isPartial);
  }

  // ─── Modal ─────────────────────────────────────────────────────────────────

  openModal() {
    this._modal.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this._boundCloseOnEsc);
    trapFocus(this._modal, this._modal.querySelector('.bt-leasing__close-btn'));
    this._hideError();
  }

  closeModal() {
    this._modal.setAttribute('hidden', '');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this._boundCloseOnEsc);
    removeTrapFocus(this._openBtn || this._editBtn);
    this._hideError();
  }

  // ─── Provider DSL ──────────────────────────────────────────────────────────

  _parseDSL(raw) {
    if (!raw) return [];
    return raw
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const parts = line.split('|').map((p) => p.trim());
        if (!parts[0]) return null;
        const label = parts[0];
        const type = ['text', 'email', 'tel', 'number', 'select', 'date'].includes(parts[1])
          ? parts[1]
          : 'text';
        const required = parts[2] === 'req';
        const options = type === 'select' && parts[3]
          ? parts[3].split(',').map((o) => o.trim()).filter(Boolean)
          : [];
        const name = 'Leasing - ' + label;
        return { label, type, required, options, name };
      })
      .filter(Boolean);
  }

  _onProviderChange() {
    const selectedName = this._providerSelect.value;
    const provider = this._providers.find((p) => p.name === selectedName);

    // Clear existing dynamic fields
    this._providerFieldsEl.replaceChildren();

    if (!provider) {
      this._validate();
      return;
    }

    const fragment = document.createDocumentFragment();

    // Optional provider intro
    const introTemplate = this.querySelector(
      `template[data-leasing-provider-intro="${provider.id}"]`
    );
    if (introTemplate) {
      const intro = document.createElement('div');
      intro.className = 'bt-leasing__provider-intro';
      intro.appendChild(introTemplate.content.cloneNode(true));
      fragment.appendChild(intro);
    }

    const fields = this._parseDSL(provider.fields);
    fields.forEach((field) => {
      const group = document.createElement('div');
      group.className = 'bt-leasing__field-group';

      const id = 'bt-leasing-field-' + this._slugify(field.label);

      const label = document.createElement('label');
      label.className = 'bt-leasing__label';
      label.htmlFor = id;
      label.textContent = field.label;
      if (field.required) {
        const req = document.createElement('span');
        req.className = 'bt-leasing__required';
        req.setAttribute('aria-hidden', 'true');
        req.textContent = ' *';
        label.appendChild(req);
      }

      let input;
      if (field.type === 'select') {
        input = document.createElement('select');
        input.className = 'bt-leasing__select';
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = '— ' + field.label + ' —';
        placeholder.disabled = true;
        placeholder.hidden = true;
        placeholder.selected = true;
        input.appendChild(placeholder);
        field.options.forEach((opt) => {
          const option = document.createElement('option');
          option.value = opt;
          option.textContent = opt;
          input.appendChild(option);
        });
      } else {
        input = document.createElement('input');
        input.className = 'bt-leasing__input';
        input.type = field.type;
      }

      input.id = id;
      input.name = field.name;
      if (field.required) {
        input.required = true;
        input.setAttribute('aria-required', 'true');
      }

      // Rehydrate saved value
      const savedVal = this._savedAttributes[field.name];
      if (savedVal) input.value = savedVal;

      group.appendChild(label);
      group.appendChild(input);
      fragment.appendChild(group);
    });

    this._providerFieldsEl.appendChild(fragment);
    this._validate();
  }

  _slugify(str) {
    return str
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  // ─── Validation ────────────────────────────────────────────────────────────

  _validate() {
    const hasProvider = Boolean(this._providerSelect?.value);
    const allRequiredFilled = Array.from(
      this._providerFieldsEl.querySelectorAll('[required]')
    ).every((el) => el.value.trim() !== '');

    const valid = hasProvider && allRequiredFilled;
    if (this._submitBtn) this._submitBtn.disabled = !valid;

    // Mark invalid fields
    this._providerFieldsEl.querySelectorAll('[required]').forEach((el) => {
      if (el.value.trim() === '') {
        el.setAttribute('aria-invalid', 'true');
      } else {
        el.removeAttribute('aria-invalid');
      }
    });

    return valid;
  }

  // ─── Rehydrate ─────────────────────────────────────────────────────────────

  _rehydrate() {
    const currentProvider = this.dataset.currentProvider;
    if (currentProvider && this._providerSelect) {
      this._providerSelect.value = currentProvider;
      this._onProviderChange();
    }
  }

  // ─── Cart AJAX ─────────────────────────────────────────────────────────────

  async _submit() {
    if (!this._validate()) return;

    this._submitBtn.disabled = true;
    this._hideError();

    const provider = this._providerSelect.value;
    const attributes = { [this._triggerAttribute]: provider };
    const previousAttributes = {
      ...this._savedAttributes,
      [this._triggerAttribute]: this.dataset.currentProvider || '',
    };
    let feeAdded = false;
    let attributesUpdated = false;

    // Collect all dynamic field values
    this._providerFieldsEl.querySelectorAll('input, select').forEach((el) => {
      if (el.name) attributes[el.name] = el.value;
    });

    try {
      // Add the fee before attributes so an invalid fee configuration cannot
      // leave a cart marked for leasing.
      if (this._feeVariantId) {
        feeAdded = await this._ensureFeeInCart();
      }

      const cart = await this._updateCart({ attributes });
      attributesUpdated = true;

      const attributesPersisted = Object.entries(attributes).every(
        ([key, value]) => cart.attributes?.[key] === value
      );
      const feePersisted = !this._feeVariantId || cart.items.some(
        (item) => String(item.variant_id) === String(this._feeVariantId)
      );
      if (!attributesPersisted || !feePersisted) {
        throw new Error('leasing cart state was not persisted');
      }

      this.dataset.leasingActive = 'true';
      this.dataset.isPartial = 'false';
      this.dataset.currentProvider = provider;
      this._savedAttributes = { ...attributes };

      this._renderActiveState(provider);
      this.closeModal();

      // Notify Dawn cart sections to re-render
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bt-leasing' });
    } catch (err) {
      if (feeAdded || attributesUpdated) {
        const rollbackAttributes = {};
        new Set([...Object.keys(previousAttributes), ...Object.keys(attributes)]).forEach((key) => {
          rollbackAttributes[key] = previousAttributes[key] || '';
        });
        const rollbackBody = { attributes: rollbackAttributes };
        if (feeAdded) rollbackBody.updates = { [this._feeVariantId]: 0 };

        try {
          await this._updateCart(rollbackBody);
        } catch (rollbackError) {
          // Keep the original error visible; the page-load consistency guard
          // will retry cleanup if rollback also fails.
        }
      }
      this._showError(this._strings.cart_error || '');
      this._submitBtn.disabled = false;
    }
  }

  async _remove() {
    this._clearAttributes();
  }

  async _clearAttributes() {
    const attributes = { [this._triggerAttribute]: '' };

    // Clear all known field attributes by inspecting saved attributes
    Object.keys(this._savedAttributes).forEach((key) => {
      attributes[key] = '';
    });

    const updates = {};
    if (this._feeVariantId) updates[this._feeVariantId] = 0;

    try {
      await this._updateCart({ attributes, updates });

      this.dataset.leasingActive = 'false';
      this.dataset.isPartial = 'false';
      this.dataset.currentProvider = '';
      this._savedAttributes = {};

      this._renderIdleState();
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bt-leasing' });
    } catch (err) {
      this._showError(this._strings.cart_error || '');
    }
  }

  async _updateCart(body) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error('cart/update.js failed');
    return response.json();
  }

  async _ensureFeeInCart() {
    const cart = this._cartCache || (await fetch('/cart.js').then((r) => r.json()));
    const feeItem = cart.items.find((item) => String(item.variant_id) === String(this._feeVariantId));

    if (feeItem) {
      // Already in cart — ensure qty is 1
      if (feeItem.quantity !== 1) {
        await this._updateCart({ updates: { [this._feeVariantId]: 1 } });
      }
      return false;
    } else {
      // Add it
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: this._feeVariantId, quantity: 1 }),
      });
      if (!response.ok) throw new Error('cart/add.js failed');
      return true;
    }
  }

  // ─── DOM State Mutations ───────────────────────────────────────────────────

  _renderActiveState(provider) {
    const triggerArea = this.querySelector('.bt-leasing__trigger-area');
    if (!triggerArea) return;

    const activeState = document.createElement('div');
    activeState.className = 'bt-leasing__active-state';
    activeState.setAttribute('aria-live', 'polite');

    const activeLabel = document.createElement('span');
    activeLabel.className = 'bt-leasing__active-label';
    activeLabel.appendChild(document.createTextNode(`${this._strings.leasing_selected || ''} \u2014 `));

    const providerName = document.createElement('strong');
    providerName.textContent = provider;
    activeLabel.appendChild(providerName);

    this._editBtn = document.createElement('button');
    this._editBtn.type = 'button';
    this._editBtn.className = 'bt-leasing__edit-btn button button--tertiary';
    this._editBtn.textContent = this._strings.edit || '';

    this._removeBtn = document.createElement('button');
    this._removeBtn.type = 'button';
    this._removeBtn.className = 'bt-leasing__remove-btn button button--tertiary';
    this._removeBtn.textContent = this._strings.remove || '';

    activeState.append(activeLabel, this._editBtn, this._removeBtn);
    triggerArea.replaceChildren(activeState);

    this._editBtn.addEventListener('click', () => this.openModal());
    this._removeBtn.addEventListener('click', () => this._remove());
  }

  _renderIdleState() {
    const triggerArea = this.querySelector('.bt-leasing__trigger-area');
    if (!triggerArea) return;
    const eligible = this.dataset.eligible === 'true';

    this._openBtn = document.createElement('button');
    this._openBtn.type = 'button';
    this._openBtn.className = 'bt-leasing__open-btn button button--secondary';
    this._openBtn.setAttribute('aria-haspopup', 'dialog');
    this._openBtn.toggleAttribute('hidden', !eligible);
    this._openBtn.textContent = this._strings.button_label || '';
    triggerArea.replaceChildren(this._openBtn);

    this._openBtn.addEventListener('click', () => this.openModal());

    // Reset form state
    if (this._providerSelect) this._providerSelect.value = '';
    this._providerFieldsEl.replaceChildren();
    if (this._submitBtn) this._submitBtn.disabled = true;
  }

  _renderPartialWarning(isPartial) {
    let warningEl = this.querySelector('.bt-leasing__partial-warning');
    if (isPartial) {
      if (!warningEl) {
        warningEl = document.createElement('p');
        warningEl.className = 'bt-leasing__partial-warning';
        warningEl.setAttribute('role', 'alert');
        this.querySelector('.bt-leasing__trigger-area')?.appendChild(warningEl);
      }
      warningEl.textContent =
        this._strings.partial_state_warning || '';
    } else if (warningEl) {
      warningEl.remove();
    }
  }

  // ─── Cart state ─────────────────────────────────────────────────────────────

  _getCartState(items) {
    let hasEligible = false;
    let hasNonEligible = false;
    const eligibleTitles = [];

    for (const item of items) {
      const isFeeItem = (this._feeVariantId && String(item.variant_id) === String(this._feeVariantId))
        || (this._feeSku && item.sku === this._feeSku);
      if (isFeeItem) continue;
      if (!item.sku) continue; // no-SKU items ignored (consistent with Liquid)

      if (this._eligibleSkus.includes(item.sku)) {
        hasEligible = true;
        if (!eligibleTitles.includes(item.title)) eligibleTitles.push(item.title);
      } else {
        hasNonEligible = true;
      }
    }

    if (hasEligible && !hasNonEligible) return { state: 'only-eligible', eligibleTitles };
    if (hasEligible && hasNonEligible)  return { state: 'mixed', eligibleTitles };
    if (!hasEligible && hasNonEligible) return { state: 'only-non-eligible', eligibleTitles: [] };
    return { state: 'empty', eligibleTitles: [] };
  }

  _applyCartState(state, eligibleTitles) {
    this.dataset.cartState = state;

    if (state === 'empty') {
      this.classList.add('bt-leasing--hidden');
    } else {
      this.classList.remove('bt-leasing--hidden');
    }

    const eligible = state === 'only-eligible';
    this.dataset.eligible = eligible ? 'true' : 'false';
    this._openBtn?.toggleAttribute('hidden', !eligible);
    this.querySelector('.bt-leasing__wrapper')?.toggleAttribute('hidden', !eligible);

    this._renderNotices(state, eligibleTitles);
  }

  _renderNotices(state, eligibleTitles) {
    const noticeEl = this.querySelector('.bt-leasing__notice');
    if (!noticeEl) return;

    noticeEl.replaceChildren();

    if (state === 'mixed') {
      const productNames = eligibleTitles.join(', ');
      const text = this._mixedNoticeTemplate.replace('{product}', productNames);
      const p = document.createElement('p');
      p.className = 'bt-leasing__notice-text';
      p.textContent = text;
      noticeEl.appendChild(p);
      noticeEl.removeAttribute('hidden');
    } else if (state === 'only-non-eligible') {
      const noticeTemplate = this.querySelector('template[data-leasing-non-eligible-notice]');
      if (noticeTemplate) noticeEl.appendChild(noticeTemplate.content.cloneNode(true));
      noticeEl.removeAttribute('hidden');
    } else {
      noticeEl.setAttribute('hidden', '');
    }
  }

  // ─── Error helpers ─────────────────────────────────────────────────────────

  _showError(message) {
    if (!this._errorEl) return;
    this._errorEl.textContent = message;
    this._errorEl.removeAttribute('hidden');
  }

  _hideError() {
    if (!this._errorEl) return;
    this._errorEl.setAttribute('hidden', '');
    this._errorEl.textContent = '';
  }

}

customElements.define('bt-leasing', BTLeasing);